/**
 * The platform settings snapshot conditional help text branches on
 * (`GET /api/help/context`, public; plan Step B2).
 *
 * BARE `axios`, NOT `@/axios` (the `apiReleaseService.js` pattern). The
 * endpoint is public, so the app instance's session bearer and its
 * `application/vnd.api+json` Content-Type would only turn a simple
 * cross-origin GET into a CORS-preflighted one for no benefit.
 */
import axios from "axios";
import { currentBearer } from "@/router/sessionBearer";

// How long a help page waits for the snapshot before showing the article
// without it (conditional text then resolves to its neutral wording).
export const HELP_CONTEXT_TIMEOUT_MS = 3000;

export function helpContextUrl(apiBaseUrl) {
  const base = String(apiBaseUrl || "").replace(/\/+$/, "");
  return `${base}/help/context`;
}

/**
 * Resolves to the context object, or `null` when it could not be read
 * (network, 4xx/5xx, timeout, a non-object body). Never rejects and never
 * waits longer than HELP_CONTEXT_TIMEOUT_MS: the caller is a help page that
 * must render its article regardless.
 */
export async function fetchHelpContext(apiBaseUrl = process.env.API_BASE_URL) {
  let timer;
  const deadline = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), HELP_CONTEXT_TIMEOUT_MS);
  });
  const request = axios
    .get(helpContextUrl(apiBaseUrl), { timeout: HELP_CONTEXT_TIMEOUT_MS })
    .then(({ data }) =>
      data && typeof data === "object" && !Array.isArray(data) ? data : null
    )
    .catch(() => null);
  try {
    return await Promise.race([request, deadline]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The owner-scoped effective platform fee rate (plan Step B4):
 * `GET /api/distributor/platform-fee-rate/effective`.
 *
 * AUTHENTICATED, yet still BARE axios with the bearer set by hand, NOT the
 * `@/axios` app instance. That instance's response interceptor treats any 401
 * as a dead session (logout + redirect to /login) and toasts errors, and this
 * read is OPTIONAL page decoration: a Builder reading owner help gets a 403
 * from it, and that must change nothing but the fallback text. The bearer
 * itself comes from the same `currentBearer()` the instance uses, so there is
 * still one definition of "the session's token". The call sends the bearer
 * only: no id, no params (the server derives the distributor from the
 * session and would ignore one anyway).
 *
 * (A request carrying Authorization is CORS-preflighted whichever client sends
 * it; the runner scenario declares `preflighted: true` for that reason.)
 *
 * Resolves to the rate as a FRACTION (0 is a real, configured rate), or `null`
 * for everything else: no session, 401/403/5xx, timeout, a malformed or
 * negative value. Never rejects, never waits past HELP_CONTEXT_TIMEOUT_MS.
 */
export function effectiveFeeRateUrl(apiBaseUrl) {
  const base = String(apiBaseUrl || "").replace(/\/+$/, "");
  return `${base}/distributor/platform-fee-rate/effective`;
}

export async function fetchEffectiveFeeRate(
  apiBaseUrl = process.env.API_BASE_URL
) {
  const bearer = currentBearer();
  if (!bearer) return null;

  let timer;
  const deadline = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), HELP_CONTEXT_TIMEOUT_MS);
  });
  const request = axios
    .get(effectiveFeeRateUrl(apiBaseUrl), {
      timeout: HELP_CONTEXT_TIMEOUT_MS,
      headers: { Authorization: `Bearer ${bearer}` },
    })
    .then(({ data }) => {
      const rate = data && typeof data === "object" ? data.platform_fee_rate : null;
      return typeof rate === "number" && Number.isFinite(rate) && rate >= 0
        ? rate
        : null;
    })
    .catch(() => null);
  try {
    return await Promise.race([request, deadline]);
  } finally {
    clearTimeout(timer);
  }
}
